<script>
  import { fade } from "svelte/transition";
  import { onDestroy } from "svelte";
  import t from "#lib/i18n.svelte.js";
  import {
    ACCEPTED_IMAGE_FILE_TYPES,
    create_image_variant_status_poller,
    prettify_file_name,
    preprocess_media,
    upload_with_progress,
    MAX_VIDEO_DIMENSION,
    MAX_VIDEO_FILE_SIZE,
    MAX_IMAGE_FILE_SIZE,
    MAX_VIDEO_DURATION
  } from "#lib/util.js";
  import TextWithLabel from "#lib/TextWithLabel.svelte";
  import ZoomableImage from "#lib/ZoomableImage.svelte";
  import ButtonLabel from "#lib/ButtonLabel.svelte";
  import Switch from "#lib/Switch.svelte";
  import FocalPointSetter from "#lib/FocalPointSetter.svelte";
  import FocalPointPreview from "#lib/FocalPointPreview.svelte";
  import SafeRegionOverlay from "#lib/SafeRegionOverlay.svelte";
  import FileDragAndDrop from "#lib/FileDragAndDrop.svelte";
  import BlockGrid from "#lib/BlockGrid.svelte";
  import Alert from "#lib/Alert.svelte";
  import { getContext } from "svelte";
  import { lazy } from '#lib/lazy.svelte.js';
  // Crop mode is editing-only: split out, prefetched once editing starts
  const load_crop_session = () => import('#lib/ImageCropSession.svelte.js');
  const image_cropper = lazy(() => import('#lib/ImageCropper.svelte'));
  let { entry, is_editing } = $props();
  let site_settings = getContext('site_settings');
  let progress = $state(0);
  let preview_blob_url = $state(null);

  // Raster masters /api/crop-image can cut — never SVG or video
  const CROPPABLE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'heic', 'heif']);
  let can_crop = $derived(
    is_editing && entry.media_type === 'image' && Boolean(entry.width && entry.height) &&
    CROPPABLE_EXTENSIONS.has((entry.original_src || entry.src)?.split('.').pop().toLowerCase())
  );
  let crop_session = $derived(entry.crop_session);
  // In crop mode the canvas shows the uncropped master under the crop frame
  let media = $derived(crop_session
    ? { media_type: 'image', alt: entry.alt, ...crop_session.source, fit_to_frame: false }
    : entry);
  let aspect_ratio = $derived(media.width / media.height);

  let error_messages = $state([]);
  let is_optimizing = $state(false);
  let variant_status_poller = $state();

  /**
   * Stop variant status polling and clear state.
   * @returns {void}
   */
  function stop_variant_status_polling() {
    variant_status_poller?.stop?.();
    variant_status_poller = undefined;
    is_optimizing = false;
  }

  /**
   * Poll image variant status and update local variants as they become available.
   * @param {string} asset_id
   * @returns {void}
   */
  function start_variant_status_polling(asset_id) {
    if (!asset_id) return;
    stop_variant_status_polling();
    is_optimizing = true;
    variant_status_poller = create_image_variant_status_poller({
      asset_id,
      on_status: (status) => {
        if (Array.isArray(status?.variants)) {
          entry.variants = status.variants;
        }
      },
      on_complete: () => {
        is_optimizing = false;
      },
      on_error: (err) => {
        console.error('Image variant status polling failed:', err);
      }
    });
  }

  onDestroy(() => {
    stop_variant_status_polling();
    if (preview_blob_url) URL.revokeObjectURL(preview_blob_url);
    entry.crop_session = null;
  });

  // Fetch crop mode while the admin is still looking, so the first click is instant
  $effect(() => {
    if (!can_crop) return;
    load_crop_session();
    image_cropper.load();
    import('#lib/ImageCropTools.svelte');
  });

  // Leaving edit mode (cancel or save) also leaves crop mode
  $effect(() => {
    if (!is_editing && entry.crop_session) entry.crop_session = null;
  });

  async function open_crop() {
    const { default: ImageCropSession } = await load_crop_session();
    entry.crop_session = new ImageCropSession(entry, {
      on_cropped: (asset_id) => start_variant_status_polling(asset_id),
    });
  }

  // Resume the "optimizing" state after a reload: derived from the server's
  // pending-job count rather than remembered — a page refresh mid-optimization
  // picks the indicator (and polling) back up instead of losing it.
  $effect(() => {
    if (!is_editing) return;
    const src = entry.src;
    if (!src?.startsWith('/assets/images/') || entry.media_type !== 'image') return;
    if (variant_status_poller) return;
    const asset_id = src.replace('/assets/', '');
    fetch(`/api/image-variant-status?asset_id=${encodeURIComponent(asset_id)}`)
      .then(response => response.ok ? response.json() : null)
      .then(status => {
        if (status && (status.pending ?? 0) > 0) {
          start_variant_status_polling(asset_id);
        }
      })
      .catch(() => { /* status probe is best-effort */ });
  });

  function handle_upload_error(errors_array) {
    const messages = errors_array.map(error_code => {
      switch (error_code) {
        case 'invalid_video_format':
          return t('invalid_video_format');
        case 'video_resolution_too_high':
          return t('video_resolution_too_high', { dimension_limit: MAX_VIDEO_DIMENSION });
        case 'video_duration_too_long':
          return t('video_duration_too_long', { duration_limit: MAX_VIDEO_DURATION });
        case 'video_file_too_large':
          return t('video_file_too_large', { size_limit_mb: MAX_VIDEO_FILE_SIZE / 1000000, dimension_limit: MAX_VIDEO_DIMENSION });
        case 'image_file_too_large':
          return t('image_file_too_large', { size_limit_kb: MAX_IMAGE_FILE_SIZE / 1024 });
        case 'unsupported_image_format_on_browser':
          return t('unsupported_image_format_on_browser');
        case 'video_metadata_error':
          return t('video_metadata_error');
        case 'unsupported_file_type':
          return t('unsupported_file_type');
        default:
          return t('unknown_upload_error');
      }
    });
    error_messages = messages;
  }

  async function replace_media(image_file) {
    let file = image_file instanceof File ? image_file : entry.file_input.files[0];
    error_messages = []; // Clear previous errors
    entry.crop_session = null;
    stop_variant_status_polling();
    const result = await preprocess_media(file);

    if (result && result.errors && result.errors.length > 0) {
      handle_upload_error(result.errors);
      if (entry.file_input) entry.file_input.value = null; // Reset file input
      return; // Stop further processing
    }

    if (result && result.optimized_file && result.metadata) {
      const { optimized_file, metadata } = result;
      const { media_type, width, height, alt, duration, audio_track } = metadata;

      // Instant local preview — rendered via `preview_blob_url`, never assigned to
      // entry.src: the model only ever receives durable /assets/… paths, so a failed
      // or interrupted upload can't leave a broken blob: URL to be saved.
      if (preview_blob_url) URL.revokeObjectURL(preview_blob_url);
      preview_blob_url = URL.createObjectURL(optimized_file);

      try {
        // NOTE asset_id equals file path relative to `/assets/` (e.g. `images/abc123234234.jpg`)
        const upload_result = await upload_with_progress(optimized_file, metadata, (p) => progress = p);
        const asset_id = upload_result.path;

        // Commit metadata to the model only after the bytes are durably stored
        if (!entry.title) {
          entry.title = prettify_file_name(file.name.split('.')[0])
        }
        entry.media_type = media_type;
        entry.width = width;
        entry.height = height;
        entry.variants = [];
        // New media: a crop of the previous image no longer applies
        entry.crop = undefined;
        entry.original_src = undefined;
        entry.original_width = undefined;
        entry.original_height = undefined;
        if (alt) entry.alt = alt;
        if (media_type === 'video') {
          entry.duration = duration;
          entry.audio_track = audio_track;
        }
        entry.src = `/assets/${asset_id}`;
        // Computed defaults from the upload-time analysis: proposed, not imposed —
        // the auto flags clear permanently on the first manual adjustment.
        if (upload_result.analysis) {
          entry.focal_point_x = upload_result.analysis.focal_point_x ?? entry.focal_point_x;
          entry.focal_point_y = upload_result.analysis.focal_point_y ?? entry.focal_point_y;
          entry.focal_point_auto = true;
          if (upload_result.analysis.graphic) {
            entry.fit_to_frame = true; // graphics/text: show whole image by default
            entry.fit_auto = true;
          }
        }
        if (media_type === 'image') {
          start_variant_status_polling(asset_id);
        }
      } catch (err) {
        console.error('Upload failed:', err);
        error_messages = [t('upload_failed')];
      } finally {
        progress = 0;
        if (preview_blob_url) {
          URL.revokeObjectURL(preview_blob_url);
          preview_blob_url = null;
        }
        if (entry.file_input) entry.file_input.value = null;
      }
    } else {
      // This case should ideally not be reached if preprocess_media always returns an object
      // or undefined only for truly unexpected errors not caught by validation.
      error_messages = [t('unknown_upload_error')];
      console.error('Failed to preprocess media or an unexpected issue occurred.');
      if (entry.file_input) entry.file_input.value = null; // Reset file input
    }
  }

  /** Single dispatcher for drop, file picker and paste — the first accepted
      file replaces this entry's media. (Batch uploads live on /new/media.) */
  function handle_files(files) {
    const list = Array.from(files || []).filter(file => ACCEPTED_IMAGE_FILE_TYPES.includes(file.type));
    if (list.length === 0) return;
    if (entry.file_input) entry.file_input.value = null;
    replace_media(list[0]);
  }

  /** Pasting an image anywhere on the page uploads it — same path as drop/select.
      (The Entry-level paste handler only serves entries with a block body.) */
  function handle_window_paste(event) {
    if (!is_editing) return;
    const files = Array.from(event.clipboardData?.files || [])
      .filter(file => ACCEPTED_IMAGE_FILE_TYPES.includes(file.type));
    if (files.length === 0) return; // plain text paste stays untouched
    event.preventDefault();
    handle_files(files);
  }

  function trigger_upload() {
    is_editing && !entry.src && entry.file_input?.click();
  }

  // TODO: mobile timeout: hide 2s after last touch
  // IDEA: use stablediffusion to extend image beyond crop
</script>

<svelte:window onpaste={handle_window_paste} />

{#if progress}
  <div>Upload progress: {progress}%</div>
{/if}

<div class='image-entry'>
  <FileDragAndDrop
    enabled={is_editing}
    handle_files_drop={handle_files}
    accept={ACCEPTED_IMAGE_FILE_TYPES}
  >
    <BlockGrid>
      
      <div 
        class="image-container flex-column items-center"
        class:extreme-landscape={aspect_ratio >= 2}
        class:extreme-portrait={aspect_ratio < 0.3334}
        class:portrait={aspect_ratio <= 0.7}
      >
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div
            in:fade={{duration: 130, delay: 130}} out:fade={{duration: 130}}
            class="image-preview border-md"
            class:sized={Boolean(media.width)}
            style:aspect-ratio={aspect_ratio}
            style:--image-width={media.width ? `${media.width}px` : undefined}
            style:--image-ratio={aspect_ratio}
            {...(is_editing ? {
              role: "button",
              tabindex: "0",
              onkeydown: (event) => {[' ','Enter'].includes(event.key) ? trigger_upload() : null},
              onclick: trigger_upload
            }: {})}
          >
            <!--
              Edit mode renders the REAL crop, focal point and variants — the admin adjusts a
              setting on the exact render it affects. `src` prefers the local preview during an
              in-flight upload; the transient blob URL never enters the model, so a failed
              upload can't be saved into the document.
            -->
            {#if !crop_session || crop_session.is_ready}
            <ZoomableImage
              {...media}
              src={preview_blob_url || media.src}
              {is_editing}
              {aspect_ratio}
              loading='eager'
              style={`max-height: 100%; width: ${entry.media_type === 'video' ? '100%' : 'auto'}; max-width: ${media.width ? `min(100%, ${media.width}px)` : '100%'}; height: auto;`}              zoom={!is_editing && entry.media_type === 'image'}
              checkerboard={is_editing}
              show_playback_control={true}
              placeholder={t('add_an_image') + ' ' + t('drag_and_drop')}
              hint={t('upload_media_hint')}
            />
            {/if}
            {#if crop_session}
              {#if crop_session.is_ready && image_cropper.component}
                <image_cropper.component session={crop_session} />
              {/if}
            {:else if is_editing && entry.src}
              <FocalPointSetter
                bind:focal_point_x={entry.focal_point_x}
                bind:focal_point_y={entry.focal_point_y}
                width={entry.width}
                height={entry.height}
                onchange={() => entry.focal_point_auto = false}
              />
              <SafeRegionOverlay
                width={entry.width}
                height={entry.height}
                focal_point_x={entry.focal_point_x}
                focal_point_y={entry.focal_point_y}
                fit_to_frame={entry.fit_to_frame}
              />

              <!-- On-image tools, top right: grouped translucent pills with
                   identical chrome — flush content, the pill carries the padding -->
              <!-- svelte-ignore a11y_no_static_element_interactions -->
              <div class="canvas-tools" onpointerdown={(e) => e.stopPropagation()} onclick={(e) => e.stopPropagation()}>
                <div class="tool-pill">
                  <button onclick={() => entry.file_input?.click()}>
                    <ButtonLabel variant='brand flush text-compact' start_icon='cycle'>{t('replace_image')}</ButtonLabel>
                  </button>
                </div>
                {#if can_crop}
                  <div class="tool-pill">
                    <button onclick={open_crop}>
                      <ButtonLabel variant='brand flush text-compact' start_icon='crop'>{t('crop')}</ButtonLabel>
                    </button>
                  </div>
                {/if}
                <div class="tool-pill">
                  <Switch
                    value={entry.fit_to_frame}
                    toggle={() => { entry.fit_to_frame = !entry.fit_to_frame; entry.fit_auto = false; }}
                  >
                    {t('whole_image')}
                  </Switch>
                </div>
              </div>
            {/if}
            {#if is_optimizing}
              <div class='optimization-indicator text-caption text-3' transition:fade={{ duration: 150 }}>
                <span class='loading-spinner' aria-hidden='true'></span>
                {t('optimizing_image')}
              </div>
            {/if}
          </div>

        <!-- Legend for the overlay: what the clear window and the stripes mean.
             In crop mode: the size the crop will have. -->
        {#if is_editing && entry.src}
          <div class="overlay-legend flex-row flex-wrap items-center gap-x g-4 text-caption" in:fade={{duration: 130, delay: 130}}>
            {#if crop_session}
              <span class="crop-size">{crop_session.result.width} × {crop_session.result.height} px</span>
            {:else if entry.fit_to_frame}
              <span class="legend-item flex-row items-center">
                <span class="swatch swatch-clear" aria-hidden="true"></span>
                {t('never_cropped')}
              </span>
            {:else}
              <span class="legend-item flex-row items-center">
                <span class="swatch swatch-clear" aria-hidden="true"></span>
                {t('visible_in_all_formats', { count: site_settings?.aspect_ratios?.length ?? 0 })}
              </span>
              <span class="legend-item flex-row items-center">
                <span class="swatch swatch-striped" aria-hidden="true"></span>
                {t('cropped_by_some_formats')}
              </span>
            {/if}
          </div>
        {/if}

          {#if error_messages.length > 0}
            <div class="error-message-container flex-column gap-y g-2 py p-4">
              {#each error_messages as error_message}
                <Alert variant="error" class='text-caption'>{error_message}</Alert>
              {/each}
            </div>
          {/if}

        <!-- Crop readout: how this image crops at every brand ratio — a strip
             below the canvas, not a mode; the focal point stays adjustable. -->
        {#if is_editing && entry.src && entry.media_type === 'image' && !crop_session}
          <details class="crop-readout full-width" in:fade={{duration: 130, delay: 130}}>
            <summary>
              <ButtonLabel variant='outlined' start_icon='eye'>{t('all_formats')}</ButtonLabel>
            </summary>
            <FocalPointPreview {...entry} />
          </details>
        {/if}
      </div>

      {#if entry.title || entry.caption || is_editing}

        <!-- Placeholder-only fields, identical to story blocks: no floating labels -->
        <div class="image-details flex-column gap-y g-8">
          <TextWithLabel
            editable={is_editing}
            variant="heading1"
            placeholder={t("title")}
            bind:content={entry.title}
          />

          {#if is_editing || entry.preamble}
            <TextWithLabel
              editable={is_editing}
              variant="preamble"
              placeholder={t("for_example_image_credits")}
              bind:content={entry.preamble}
              />
          {/if}

          {#if is_editing || entry.description}
            <TextWithLabel
              editable={is_editing}
              variant="body"
              placeholder={t('image_caption_suggestion')}
              bind:content={entry.description}
            />
          {/if}

          {#if is_editing}
            <TextWithLabel
              editable={is_editing}
              variant="caption"
              placeholder={t("image_alt_label")}
              bind:content={entry.alt}
            />
          {/if}

        </div>
      {/if}

    </BlockGrid>
  </FileDragAndDrop>
</div>

<!-- file input rendered off-screen -->
<input
  style="position: fixed; left: -10000px;"
  type="file"
  accept={ACCEPTED_IMAGE_FILE_TYPES.join(',')}
  name="mediafile"
  bind:this={entry.file_input}
  onchange={(event) => handle_files(event.target.files)}
/>

<style lang="postcss">
  .optimization-indicator {
    position: absolute;
    right: var(--size-2);
    bottom: var(--size-2);
    z-index: 4;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: var(--size-2);
    padding: var(--size-1) var(--size-2);
    border-radius: var(--radius-sm);
    background: color-mix(in oklab, var(--color-default-background) 75%, transparent);
    backdrop-filter: blur(4px);
  }

  /* Optimization spinner: the shared .loading-spinner from animations.css */

  .image-entry {
    min-height: calc(100vh - var(--dynamic-island-bottom-edge));
  }
  .image-details {
    grid-area: 1 / 27 / 2 / 50;
    padding-block: var(--block-spacing-s);
    @media (max-width: 768px) {
      grid-area: 2 / 2 / 3 / 50;
    }
  }
  .image-container {
    padding-block: var(--block-spacing-s);
    grid-area: 1 / 2 / 2 / 25;
    @media (max-width: 768px) {
      grid-area: 1 / 2 / 2 / 50;
      padding-block-end: 0;
    }
  }
  /* Image always left, text always right on desktop — including panoramic
     images (they render smaller in the left column rather than full-width). */
  .image-preview {
    /* Calmer, larger transparency checker on the editing canvas — the default
       10px tile visually clashes with the diagonal overlay stripes */
    :global(.checkerboard) {
      --checker-size: 32px;
    }

    --preview-max-height: calc(100vh - var(--dynamic-island-bottom-edge) - 2 * var(--block-spacing-s));
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
    max-width: 100%;
    max-height: var(--preview-max-height);
    @media (max-width: 768px) {
      max-height: none;
    }
    border-radius: 0;
    /* The box always has the image's own ratio, as wide as the column, the
       image and the height limit allow. Squeezed by max-height alone it took
       another ratio, so the image's own page cut it (or letterboxed it, with
       "Ganzes Bild") and overlays such as the crop frame missed the picture. */
    &.sized {
      width: min(100%, var(--image-width), calc(var(--preview-max-height) * var(--image-ratio)));
      @media (max-width: 768px) {
        width: min(100%, var(--image-width));
      }
    }
    &:global(:has(.placeholder-image)){
      width: 100%;
      min-height: 200px;
    }
  }

  .extreme-portrait .image-preview {
    --preview-max-height: calc(100vh - var(--dynamic-island-bottom-edge));
  }

  /* Spans the image so the pills wrap on narrow screens instead of spilling
     past its left edge; pointer events only on the pills themselves */
  .canvas-tools {
    position: absolute;
    top: var(--size-2);
    right: var(--size-2);
    left: var(--size-2);
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: stretch;
    gap: var(--size-2);
    z-index: 4;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }
  }

  .tool-pill {
    display: flex;
    align-items: center;
    gap: var(--size-2);
    height: 40px;
    padding-inline: var(--size-3);
    background: color-mix(in oklab, var(--color-default-background) 88%, transparent);
    backdrop-filter: blur(6px);
    border: 1px solid var(--color-default-stroke);
    border-radius: var(--input-border-radius);
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.12);

    /* Flush content: the pill owns the padding, controls bring none */
    :global(.button-label) {
      padding: 0;
    }
  }

  .overlay-legend {
    width: 100%;
    margin-block-start: var(--size-2);
    justify-content: flex-end; /* legend sits bottom right, under the canvas */
    color: var(--color-default-foreground-600, inherit);

    .legend-item {
      gap: 0.5em;
    }

    .swatch {
      width: 14px;
      height: 14px;
      border-radius: 3px;
      border: 1px solid var(--color-default-stroke);
      flex-shrink: 0;
    }
    /* A texture sample, not an empty box — plain white read as a checkbox.
       The transparency checker is the material of the visible image area. */
    .swatch-clear {
      --c: oklch(from var(--color-default-foreground) l c h / 0.18);
      background-image: linear-gradient(45deg, var(--c) 25%, transparent 25%, transparent 75%, var(--c) 75%, var(--c)),
                        linear-gradient(45deg, var(--c) 25%, transparent 25%, transparent 75%, var(--c) 75%, var(--c));
      background-position: 0 0, 3px 3px;
      background-size: 6px 6px;
      background-color: var(--color-default-background);
    }
    .swatch-striped {
      background-color: color-mix(in oklab, var(--editing) 10%, transparent);
      background-image: linear-gradient(-45deg, var(--editing-800) 10%, transparent 10%, transparent 40%, var(--editing-800) 40%, var(--editing-800) 60%, transparent 60%, transparent 90%, var(--editing-800) 90%);
      background-size: 4px 4px;
    }
  }

  .crop-size {
    font-variant-numeric: tabular-nums;
  }

  .crop-readout {
    margin-block-start: var(--size-3);

    summary {
      display: inline-flex;
      cursor: pointer;
      user-select: none;
      list-style: none; /* the ButtonLabel is the whole affordance — no disclosure marker */

      &::-webkit-details-marker {
        display: none;
      }
    }
  }
</style>